import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

// Firebase modular SDK
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
    getMessaging,
    getToken,
    onMessage,
    Messaging,
    MessagePayload,
    deleteToken
} from 'firebase/messaging';

import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

export interface FcmNotification {
    title: string;
    body: string;
    icon?: string;
    image?: string;
    data?: Record<string, string>;
    timestamp: Date;
}

export type NotificationPermissionStatus = 'default' | 'granted' | 'denied' | 'unsupported';

@Injectable({
    providedIn: 'root'
})
export class FcmService {

    private readonly vapidKey = environment.vapidKey;
    private readonly http = inject(HttpClient);

    private app!: FirebaseApp;
    private messaging!: Messaging;

    /** Evita registrar onMessage múltiples veces */
    private _foregroundListenerActive = false;

    /** SW registration cacheada para mostrar notificaciones en foreground */
    private _swRegistration: ServiceWorkerRegistration | null = null;

    /** FCM token del dispositivo actual */
    private _fcmToken$ = new BehaviorSubject<string | null>(null);
    readonly fcmToken$ = this._fcmToken$.asObservable();

    /** Stream de notificaciones recibidas en foreground */
    private _notification$ = new BehaviorSubject<FcmNotification | null>(null);
    readonly notification$ = this._notification$.asObservable();

    /** Estado del permiso de notificaciones */
    private _permission$ = new BehaviorSubject<NotificationPermissionStatus>('default');
    readonly permission$ = this._permission$.asObservable();

    constructor() {
        this.initFirebase();
    }

    // ─────────────────────────────────────────────────────────────
    // Inicialización
    // ─────────────────────────────────────────────────────────────

    private initFirebase(): void {
        if (!this.isBrowserSupported()) {
            console.warn('[FCM] Push notifications no soportadas en este navegador');
            this._permission$.next('unsupported');
            return;
        }

        // Reutilizar instancia si ya existe
        const existingApps = getApps();
        this.app = existingApps.length > 0
            ? existingApps[0]
            : initializeApp(environment.firebaseConfig);

        this.messaging = getMessaging(this.app);
        this._permission$.next(Notification.permission as NotificationPermissionStatus);
    }

    // ─────────────────────────────────────────────────────────────
    // Permisos y Token
    // ─────────────────────────────────────────────────────────────

    /**
     * Solicita permiso al usuario y obtiene el FCM Token.
     * Registra el service worker automáticamente.
     * @returns Observable con el token o null si se deniega
     */
    requestPermissionAndGetToken(): Observable<string | null> {
        if (!this.isBrowserSupported()) {
            return of(null);
        }

        return from(Notification.requestPermission()).pipe(
            switchMap((permission) => {
                this._permission$.next(permission as NotificationPermissionStatus);

                if (permission !== 'granted') {
                    console.warn('[FCM] Permiso de notificaciones denegado');
                    return of(null);
                }

                return from(this.getOrRefreshToken());
            }),
            catchError((err) => {
                console.error('[FCM] Error al solicitar permiso:', err);
                return of(null);
            })
        );
    }

    /**
     * Obtiene o refresca el FCM token registrando el service worker.
     */
    private async getOrRefreshToken(): Promise<string | null> {
        try {
            const swRegistration = await this.registerServiceWorker();

            // Si el service worker no pudo registrarse, no continuar
            if (!swRegistration) {
                console.warn('[FCM] No hay service worker disponible, abortando getToken');
                return null;
            }

            // Cachear la registration para usarla en foreground
            this._swRegistration = swRegistration;

            const token = await getToken(this.messaging, {
                vapidKey: this.vapidKey,
                serviceWorkerRegistration: swRegistration
            });

            if (token) {
                console.log('[FCM] Token obtenido:', token);
                this._fcmToken$.next(token);
                this.listenForegroundMessages();
                return token;
            } else {
                console.warn('[FCM] No se pudo obtener un token');
                return null;
            }
        } catch (error) {
            console.error('[FCM] Error al obtener token:', error);
            return null;
        }
    }

    /**
     * Elimina el token FCM actual (útil al hacer logout).
     */
    async revokeToken(): Promise<void> {
        try {
            if (this.messaging) {
                await deleteToken(this.messaging);
                this._fcmToken$.next(null);
                console.log('[FCM] Token eliminado');
            }
        } catch (error) {
            console.error('[FCM] Error al eliminar token:', error);
        }
    }

    /**
     * Retorna el token actual sin solicitar permisos.
     */
    getCurrentToken(): string | null {
        return this._fcmToken$.getValue();
    }

    // ─────────────────────────────────────────────────────────────
    // Service Worker
    // ─────────────────────────────────────────────────────────────

    /**
     * Registra el firebase-messaging-sw.js como service worker.
     * Incluye timeout para evitar colgarse si el SW falla silenciosamente.
     */
    private async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
        if (!('serviceWorker' in navigator)) {
            console.warn('[FCM] Service Workers no soportados');
            return null;
        }

        try {
            // Registrar el SW (sin forzar scope '/' para evitar conflictos con Angular)
            const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log(`[FCM] SW registrado — estado: ${reg.installing ? 'installing' : reg.waiting ? 'waiting' : reg.active ? 'active' : 'unknown'}`);

            // Añadir timeout a navigator.serviceWorker.ready para evitar colgarse
            // si el SW tiene un error silencioso durante la activación
            const timeoutMs = 10_000;
            const readyWithTimeout = Promise.race([
                navigator.serviceWorker.ready,
                new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error(`[FCM] Timeout: SW no activó en ${timeoutMs}ms`)), timeoutMs)
                )
            ]);

            const activeRegistration = await readyWithTimeout;
            console.log('[FCM] Service Worker activo:', (activeRegistration as ServiceWorkerRegistration).scope);
            return activeRegistration as ServiceWorkerRegistration;
        } catch (error) {
            console.error('[FCM] Error al registrar/activar Service Worker:', error);
            return null;
        }
    }

    /**
     * Retorna el service worker actualmente registrado (si existe).
     */
    async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
        if (!('serviceWorker' in navigator)) return undefined;
        return navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    }

    // ─────────────────────────────────────────────────────────────
    // Mensajes en Foreground
    // ─────────────────────────────────────────────────────────────

    /**
     * Escucha mensajes FCM cuando la app está en primer plano.
     * Muestra una notificación nativa del sistema.
     */
    private listenForegroundMessages(): void {
        if (!this.messaging) return;

        // Evitar registrar el listener múltiples veces
        if (this._foregroundListenerActive) {
            console.log('[FCM] Listener de foreground ya activo, omitiendo registro duplicado');
            return;
        }
        this._foregroundListenerActive = true;

        onMessage(this.messaging, async (payload: MessagePayload) => {
            console.log('[FCM] Mensaje en foreground recibido:', payload);

            const notification: FcmNotification = {
                title: payload.notification?.title || payload.data?.['title'] || 'Nueva notificación',
                body: payload.notification?.body || payload.data?.['body'] || '',
                icon: payload.notification?.icon || '/favicon.ico',
                image: payload.notification?.image || payload.data?.['image'],
                data: payload.data,
                timestamp: new Date()
            };

            // Emitir al stream para que los componentes puedan reaccionar
            this._notification$.next(notification);

            // Mostrar notificación usando el Service Worker (funciona en foreground)
            await this.sendNotificationToSW(notification);
        });
    }

    /**
     * Envía la notificación al SW via postMessage para que sea
     * el SW quien llame a self.registration.showNotification().
     * Siempre intentamos mostrar: el toast in-app + la notificación nativa del OS.
     */
    private async sendNotificationToSW(notification: FcmNotification): Promise<void> {
        if (Notification.permission !== 'granted') return;

        try {
            const registration = this._swRegistration ?? await navigator.serviceWorker.ready;
            this._swRegistration = registration;
            const controller = navigator.serviceWorker?.controller;

            const notifOptions = {
                body: notification.body,
                icon: notification.icon || '/favicon.ico',
                data: { url: notification.data?.['url'] || '/', ...notification.data },
                tag: notification.data?.['tag'] || `fcm-fg-${Date.now()}`,
                requireInteraction: false
            };

            if (controller) {
                // Enviar al SW controlador (postMessage) — más fiable en Chrome
                controller.postMessage({
                    type: 'SHOW_NOTIFICATION',
                    title: notification.title,
                    options: notifOptions
                });
                console.log('[FCM] postMessage SHOW_NOTIFICATION enviado al SW controlador ✅');
            } else {
                // Fallback: showNotification directo en la registration (funciona en main thread)
                console.warn('[FCM] Sin SW controlador, usando registration.showNotification()');
                await registration.showNotification(notification.title, notifOptions);
            }

        } catch (err) {
            console.error('[FCM] Error al enviar notificación al SW:', err);
        }
    }

    // ─────────────────────────────────────────────────────────────
    // Envío de tokens al backend
    // ─────────────────────────────────────────────────────────────

    /**
     * Registra el token en el backend para enviar notificaciones desde el servidor.
     * @param apiUrl Base URL de la API
     * @param userId ID del usuario actual
     * @param deviceId ID del dispositivo registrado (requerido por el backend)
     */
    registerTokenInBackend(apiUrl: string, userId: string, deviceId?: number): Observable<any> {
        const token = this.getCurrentToken();
        if (!token) {
            console.warn('[FCM] No hay token para registrar en backend');
            return of(null);
        }

        return this.http.post(`${apiUrl}/fcm/token`, {
            userId,
            fcmToken: token,
            platform: 'web',
            userAgent: navigator.userAgent,
            ...(deviceId ? { deviceId } : {})
        }).pipe(
            tap(() => console.log('[FCM] Token registrado en backend ✅')),
            catchError((err) => {
                console.error('[FCM] Error al registrar token en backend:', err);
                return of(null);
            })
        );
    }

    /**
     * Revoca el token FCM en el backend y en Firebase.
     * Llamar en el flujo de logout.
     * @param apiUrl Base URL de la API (sin /api)
     */
    logoutFcm(apiUrl: string): Observable<any> {
        const token = this.getCurrentToken();

        if (!token) {
            return from(this.revokeToken());
        }

        // 1. Revocar en el backend
        return this.http.delete(`${apiUrl}/fcm/token`, {
            body: { fcmToken: token }
        }).pipe(
            switchMap(() => {
                // 2. Revocar en Firebase
                return from(this.revokeToken());
            }),
            catchError((err) => {
                console.error('[FCM] Error al revocar token en backend, revocando local:', err);
                return from(this.revokeToken()); // revocar local aunque falle el backend
            })
        );
    }

    // ─────────────────────────────────────────────────────────────
    // Utilidades
    // ─────────────────────────────────────────────────────────────

    /**
     * Verifica si el navegador soporta notificaciones push.
     */
    isBrowserSupported(): boolean {
        return (
            typeof window !== 'undefined' &&
            'Notification' in window &&
            'serviceWorker' in navigator &&
            'PushManager' in window
        );
    }

    /**
     * Comprueba si el permiso ya fue otorgado previamente.
     */
    isPermissionGranted(): boolean {
        return typeof Notification !== 'undefined' && Notification.permission === 'granted';
    }

    /**
     * Retorna el estado actual del permiso.
     */
    getPermissionStatus(): NotificationPermissionStatus {
        if (!this.isBrowserSupported()) return 'unsupported';
        return Notification.permission as NotificationPermissionStatus;
    }
}